<template>
  <div>
    <div class="col-12">
      <span class="item-detail-title">{{ inspectable.name }}</span>
    </div>
    <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
        <template v-if="inspectableTypes[inspectable.type] === 'number'">
            <input
                type="number"
                step="any"
                v-model.number="result.number"
                class="form-control"
                :min="inspectable.range?.min"
                :max="inspectable.range?.max"
                :placeholder="getValueRange(inspectable)"
                :class="{ 'is-valid': isValidInput(inspectable, result.number), 'is-invalid': !isValidInput(inspectable, result.number) }"
                @change="e => { result.number = getValidNumber(inspectable, e.target.value) }"
                @blur="$emit('resultChanged')"
            />
        </template>

        <template v-else-if="inspectableTypes[inspectable.type] === 'text'">
            <input
                type="text"
                v-model="result.text"
                class="form-control"
                @change="$emit('resultChanged')"
            />
        </template>

        <template v-else-if="inspectableTypes[inspectable.type] === 'select_single'">
            <div class="form-check" v-for="(item) in inspectable.selectable_items" :key="item.id">
                <input
                    type="radio"
                    class="form-check-input"
                    :id="item.id"
                    :value="item"
                    v-model="result.select_single"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label" for="select_single">
                    {{ item.name }}
                </label>
            </div>
        </template>

        <template v-else-if="inspectableTypes[inspectable.type] === 'select_multi'">
            <div class="form-check mb-3" v-for="(item) in inspectable.selectable_items" :key="item.id">
                <input
                    type="checkbox"
                    class="form-check-input"
                    :id="item.id"
                    :value="item"
                    v-model="result.select_multi"
                    :disabled="item.notEnabled"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label ms-2" for="select_multi">
                    {{ item.name }}
                </label>
            </div>
        </template>

        <template v-else-if="inspectableTypes[inspectable.type] === 'boolean'">
            <div class="form-check">
                <input
                    type="radio"
                    class="form-check-input"
                    :id="'bool_yes'+inspectable.id"
                    :value="true"
                    v-model="result.boolean"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label" for="bool_yes">
                    {{ $t('common.yes') }}
                </label>
            </div>
            <div class="form-check">
                <input
                    type="radio"
                    class="form-check-input"
                    :id="'bool_no' + inspectable.id"
                    :value="false"
                    v-model="result.boolean"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label" for="bool_no">
                    {{ $t('common.no') }}
                </label>
            </div>
        </template>

        <template v-else-if="inspectableTypes[inspectable.type] === 'risk_assessment'">
            <div class="form-check">
                <input
                    type="radio"
                    class="form-check-input"
                    :id="'risk_yes'+inspectable.id"
                    :value="true"
                    v-model="result.boolean"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label" for="bool_yes">
                    {{ $t('common.yes') }}
                </label>
            </div>
            <div class="form-check">
                <input
                    type="radio"
                    class="form-check-input"
                    :id="'risk_no' + inspectable.id"
                    :value="false"
                    v-model="result.boolean"
                    @change="$emit('resultChanged')"
                />
                <label class="form-check-label" for="bool_no">
                    {{ $t('common.no') }}
                </label>
            </div>
        </template>

        <template v-else>
            <select
                class="form-select"
                v-model="defaultValue"
                @change="$emit('valueChanged', inspectable.id, defaultValue)"
            >
                <option
                    v-for="option in inspectionOptions"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.text }}
                </option>
            </select>
        </template>

    </div>
    <div class="col-12 nopads mt-2 mb-2 pl-3 pr-3">
    <textarea
        :id="'info'+ inspectable.id"
        v-model="info"
        class="form-control nopads p-1"
        :class="isInfoMandatory ? 'is-invalid' : 'is-valid'"
        :placeholder="$t('inspection.additional_info_placeholder')"
        rows="3"
        style="max-height: calc(1.5em * 6); overflow-y: auto;"
        @blur="$emit('resultChanged')"
    ></textarea>

    <template v-if="inspectionType !== HARJA_INSPECTION_REPORT_TYPE">
        <span class="span-title">{{ $t('equipment.photo') }}</span>

        <div class="d-flex justify-content-between align-items-center">
            <!-- Hidden file input -->
            <input
                type="file"
                class="d-none"
                :id="'photo'+inspectable.id"
                accept="image/jpeg, image/jpg, image/png"
                @change="onFileChange"
                ref="fileInput"
            />
            <span class="flex-grow-1">{{ selectedFile ? selectedFile.name :  $t('equipment.photo_placeholder') }}</span>
            <button
                type="button"
                class="btn btn-outline-primary btn-sm me-2"
                @click="triggerFileInput"
            >
                <i class="fa fa-upload me-1"></i> {{ $t('common.add_photo') }}
            </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {attachmentHelper} from '../../mixins/AttachmentMixin'
import {types as inspectableTypes} from "../../../modules/Inspectable";
import BaseModal from "@/components/BaseModal";

export default {
    name: 'InspectionResult',
    components: {BaseModal},
    mixins: [attachmentHelper],
    props: {
        inspectableItem: {
            type: Object,
            default: null
        },
        inspectionType: {
            type: Number
        },
        allowSkipping: {
            type: Boolean,
            default: function () {
                return false
            }
        }
    },
    data: function () {
        return {
            inspectableTypes,
            canSubmit: false,
            submitModal: false,
            selectedFile: null,
            inspectable: null,
            inspectionOptions: [
                {value: 1, text: this.$t('inspection.options.not_tested')},
                {value: 2, text: this.$t('inspection.options.okay')},
                {value: 3, text: this.$t('inspection.options.defects')},
                {value: 4, text: this.$t('inspection.options.ban')}
            ],
            result: {
                number: null,
                text: null,
                select_single: null,
                select_multi: [],
                boolean: null,
            },
            defaultValue: null,
            info: '',
            HARJA_INSPECTION_REPORT_TYPE: 7
        }
    },
    computed: {
        isInfoMandatory() {
            const mandatory = (this.inspectableTypes[this.inspectable?.type] === 'risk_assessment' && this.result.boolean === false)
            return mandatory && (!this.info || this.info.length < 5)
        }
    },
    created() {
        this.inspectable = JSON.parse(JSON.stringify(this.inspectableItem))
    },
    methods: {
        getInfo() {
            return this.info
        },

        onFileChange(event) {
            const file = event.target.files[0]
            if (file) {
                this.selectedFile = file
            }
        },
        triggerFileInput() {
            // Trigger the file input to open the file dialog
            this.$nextTick(() => {
                const input = this.$refs.fileInput
                if (input) {
                    input.click()
                }
            })
        },

        getValueRange(inspectable) {
            if (inspectable.range) {
                let text = inspectable.range.min === undefined ? '' : (inspectable.range.min + ' - ')
                if (inspectable.range.max !== undefined) {
                    text += inspectable.range.max
                }
                return text
            }
            return ''
        },
        getTitle: function (type) {
            switch (type) {
                case 1:
                    return this.$t('inspection_template.type.weekly')
                case 2:
                    return this.$t('inspection_template.type.monthly')
                case 3:
                    return this.$t('inspection_template.type.annually')
                case 4:
                    return this.$t('inspection_template.type.deployment')
                case 5:
                    return this.$t('inspection_template.type.daily')
            }
        },

        isValidInput(inspectable, value) {
            if (value === undefined || value === null || value.length < 1) {
                return null
            }
            return this.getValidNumber(inspectable, value) !== undefined
        },

        getValidNumber(inspectable, value) {
            if (inspectable.range && (inspectable.range.max || inspectable.range.min) && value) {
                if (inspectable.range.max !== undefined && inspectable.range.min !== undefined) {
                    if (value <= inspectable.range.max && value >= inspectable.range.min) {
                        return value
                    }
                    return undefined
                } else if (inspectable.range.max !== undefined && value <= inspectable.range.max) {
                    return value
                } else if (inspectable.range.min !== undefined && value >= inspectable.range.min) {
                    return value
                }
                return undefined
            }
            return value
        },

        getResultSet() {
            const result = JSON.parse(JSON.stringify(this.result));
            if (result.select_multi && result.select_multi.length < 1){
                result.select_multi = null
            }
            return result
        },

        getPhoto() {
            return this.selectedFile
        }

    }
}


</script>

<style>
p {
    padding-left: .5em;
    padding-right: .5em;
    font-size: 1.2em;
}

div.info {
    margin-left: 30px;
}
</style>
